// Conference Room — interactive-enhancement pure model.
//
// Layout selection, stage/participant derivation, and the honest panel registry
// live here as pure functions so the repo's node test env can exercise the real
// decision logic (no jsdom) and the room component imports one source of truth.
//
// Honesty (design packet 05_STATE_AND_HONESTY_MATRIX + 08 handoff contract):
// a tab may only look enabled when a real owner exists. People is live (the Daily
// call object). Chat/Notes/Files/Tasks/Approvals name their canonical owner and
// stay blocked until wired into the room. Transcript is deferred (recording /
// consent / retention require a separate authorized phase).

import type { CallParticipant, CallPhase } from "./call.types";

export type RoomLayout = "spotlight" | "grid" | "filmstrip" | "screen";

export interface LayoutOption {
  id: RoomLayout;
  label: string;
}

/**
 * The layouts offered by the View menu. Screen Share is only offered when
 * someone is actually presenting — never a non-functional option (packet §04).
 */
export function availableLayouts(participantCount: number, hasScreenShare: boolean): LayoutOption[] {
  const options: LayoutOption[] = [
    { id: "spotlight", label: "Spotlight" },
    { id: "grid", label: "Grid" },
    { id: "filmstrip", label: "Filmstrip" },
  ];
  if (hasScreenShare) options.unshift({ id: "screen", label: "Screen share" });
  return options;
}

/** Auto layout: screen share wins; a single participant reads as spotlight; */
/** small calls default to grid; larger calls default to spotlight+filmstrip. */
export function chooseAutoLayout(participantCount: number, hasScreenShare: boolean): RoomLayout {
  if (hasScreenShare) return "screen";
  if (participantCount <= 1) return "spotlight";
  if (participantCount <= 4) return "grid";
  // Larger calls: active speaker on the primary stage + everyone in the filmstrip.
  return "filmstrip";
}

/** Resolve the requested layout against reality: fall back off `screen` when */
/** nobody is presenting so the stage never shows an empty share. */
export function resolveLayout(requested: RoomLayout | null, participantCount: number, hasScreenShare: boolean): RoomLayout {
  if (requested === "screen" && !hasScreenShare) return chooseAutoLayout(participantCount, hasScreenShare);
  if (!requested) return chooseAutoLayout(participantCount, hasScreenShare);
  return requested;
}

/**
 * The participant that owns the primary stage in spotlight/filmstrip layouts:
 * an explicit pin wins, else the active speaker, else the first remote, else the
 * local user. Returns null when there are no participants.
 */
export function stageParticipant(
  participants: CallParticipant[],
  pinnedId: string | null,
  activeSpeakerId: string | null,
): CallParticipant | null {
  if (participants.length === 0) return null;
  if (pinnedId) {
    const pinned = participants.find((p) => p.sessionId === pinnedId);
    if (pinned) return pinned;
  }
  if (activeSpeakerId) {
    const speaker = participants.find((p) => p.sessionId === activeSpeakerId && !p.isLocal);
    if (speaker) return speaker;
  }
  const remote = participants.find((p) => !p.isLocal);
  return remote ?? participants[0];
}

export type TileState =
  | "speaking"
  | "presenting"
  | "reconnecting"
  | "camera_off"
  | "muted"
  | "video";

/** The dominant visual state for a tile, most-significant first. */
export function tileState(p: CallParticipant, reconnecting = false): TileState {
  if (reconnecting) return "reconnecting";
  if (p.screenOn) return "presenting";
  if (p.isActiveSpeaker && p.audioOn) return "speaking";
  if (!p.videoOn || !p.videoTrack) return "camera_off";
  if (!p.audioOn) return "muted";
  return "video";
}

/**
 * Remote participants whose audio must be rendered. This is deliberately
 * INDEPENDENT of the visual layout: audio elements are mounted once per remote
 * in a dedicated sink, never tied to which tiles a layout happens to render, so
 * spotlight/screen-share never silence off-stage participants. Local audio is
 * excluded (never played back).
 */
export function remoteAudioParticipants(participants: CallParticipant[]): CallParticipant[] {
  return participants.filter((p) => !p.isLocal && p.audioOn)
}

/**
 * Screen-reader announcement for a call-phase transition (aria-live region).
 * No JS in this component reacted to phase changes for assistive tech before
 * this — a phase change was silent to a screen-reader user. Pure + honest:
 * never asserts more than the phase itself proves (e.g. no "protected"
 * claim without server evidence, matching the OLIVIUM badge's own rule).
 */
export function phaseAnnouncement(phase: CallPhase, networkWarning: boolean): string {
  if (phase === 'joined') return networkWarning ? 'Connected. Network degraded.' : 'Connected.';
  if (phase === 'reconnecting') return 'Network interrupted. Reconnecting.';
  if (phase === 'joining') return 'Joining the call.';
  if (phase === 'loading_sdk') return 'Starting the call engine.';
  if (phase === 'requesting_permissions') return 'Requesting camera and microphone permission.';
  if (phase === 'preview_ready') return 'Preview ready.';
  if (phase === 'left') return 'You left the call.';
  if (phase === 'ended') return 'The call has ended.';
  if (phase === 'denied') return 'Access denied.';
  if (phase === 'expired') return 'Your join token expired.';
  if (phase === 'revoked') return 'Your access was revoked.';
  if (phase === 'locked') return 'This room is locked by the host.';
  if (phase === 'device_error') return 'A device or provider error occurred.';
  return 'Preparing the secure room.';
}

export function initialsOf(name: string): string {
  return (name || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// ── Honest panel registry ───────────────────────────────────────────────────

export type PanelStatus = "connected" | "requires_wiring" | "blocked" | "unavailable" | "deferred";

export interface PanelDef {
  key: "people" | "chat" | "notes" | "files" | "tasks" | "approvals" | "transcript";
  label: string;
  status: PanelStatus;
  /** The real Square Code owner this tab binds to (or would bind to). */
  owner: string;
  /** Shown when the tab is not `connected`: the exact dependency, honestly. */
  reason?: string;
}

/**
 * People is live off the Daily call object. Every other tab names its canonical
 * owner and stays blocked/deferred until it is actually wired into the room — no
 * placeholder is ever presented as live (packet honesty rules).
 */
export const CONFERENCE_PANELS: PanelDef[] = [
  { key: "people", label: "People", status: "connected", owner: "Daily call object + sq_meeting_participants" },
  {
    key: "chat",
    label: "Chat",
    status: "blocked",
    owner: "Square Code Communications (sq_communication_channels / sq_messages)",
    reason: "Meeting chat binds to a linked Communications channel; this room is not bound to one. Never Daily-native chat.",
  },
  {
    key: "notes",
    label: "Notes",
    status: "connected",
    owner: "sq_calendar_workspace_items (anchor_kind: meeting)",
    reason: "Migration 20260711000001 is source-ready; until an operator applies it, this tab reports backend_not_migrated honestly.",
  },
  {
    key: "files",
    label: "Files",
    status: "connected",
    owner: "api.files (Supabase Storage) + sq_calendar_workspace_items references",
    reason: "Uploads go to your own canonical storage, referenced against this meeting — not dedicated shared meeting storage.",
  },
  {
    key: "tasks",
    label: "Tasks",
    status: "connected",
    owner: "canonical task system (sq_team_tasks)",
    reason: "sq_team_tasks has no meeting-scoped column yet — this is your real task list, not a durable per-meeting ledger; tasks created here name this meeting in their title.",
  },
  {
    key: "approvals",
    label: "Approvals",
    status: "connected",
    owner: "sq_post_job_approvals (meeting_id column, migration 20260715000001)",
    reason: "Each meeting lazily owns one internal sq_post_jobs row solely to satisfy the approvals table's job_id requirement; the real link is meeting_id. Deciding requires operator role level 80+, independent of who Daily marks as call host.",
  },
  {
    key: "transcript",
    label: "Transcript",
    status: "deferred",
    owner: "n/a",
    reason: "Recording, consent, retention, and provider callback require a separate authorized phase.",
  },
];

export function panelByKey(key: PanelDef["key"]): PanelDef {
  return CONFERENCE_PANELS.find((p) => p.key === key) ?? CONFERENCE_PANELS[0];
}
